// @ts-check
import { useState, useCallback } from 'react';
import { isValidForQRCode } from '../columns';

/**
 * Owns the row-selection map for QR export and bulk operations. Only licenses
 * that are valid for QR generation can be selected (mirrors the original
 * disabled-checkbox behavior).
 *
 * @param {any[]} filteredLicenses the currently visible (filtered) licenses
 */
export function useSelection(filteredLicenses) {
  const [selectedLicenses, setSelectedLicenses] = useState(/** @type {Record<string, boolean>} */ ({}));

  const handleLicenseSelect = useCallback((serialNumber, selected) => {
    setSelectedLicenses((prev) => ({ ...prev, [serialNumber]: selected }));
  }, []);

  // Select/deselect every valid license currently on the page.
  const handleSelectAll = useCallback(
    (selected) => {
      const next = {};
      filteredLicenses.forEach((license) => {
        if (isValidForQRCode(license)) {
          next[license.serialNumber] = selected;
        }
      });
      setSelectedLicenses((prev) => ({ ...prev, ...next }));
    },
    [filteredLicenses],
  );

  // Select every valid license matching the current filter (additive).
  const handleSelectAllMatching = useCallback(() => {
    setSelectedLicenses((prev) => {
      const next = { ...prev };
      filteredLicenses.forEach((license) => {
        if (isValidForQRCode(license)) {
          next[license.serialNumber] = true;
        }
      });
      return next;
    });
  }, [filteredLicenses]);

  const getSelectedLicenses = useCallback(
    () => filteredLicenses.filter((license) => selectedLicenses[license.serialNumber]),
    [filteredLicenses, selectedLicenses],
  );

  const selectedCount = Object.values(selectedLicenses).filter(Boolean).length;

  return {
    selectedLicenses,
    setSelectedLicenses,
    handleLicenseSelect,
    handleSelectAll,
    handleSelectAllMatching,
    getSelectedLicenses,
    selectedCount,
  };
}
